/**
 * Downhole vocabularies — ONE exhaustive map per enum, keyed on the API's
 * values (downhole.routes.ts and the dh_* CHECKs), and the only place a
 * /downhole screen turns one into words or a colour.
 *
 * EXHAUSTIVE, AND AN UNKNOWN VALUE IS `—`. Every map below covers its whole
 * enum. A value outside it renders `—` on slate with a transparent rail, never
 * a guessed label. The unrouted screens fell back to "Available" for any status
 * they did not recognise, which on a tool register means "free to send out" —
 * a confident claim about a row the screen could not classify (CLAUDE.md,
 * the register idiom; EquipmentScreen's UNKNOWN_STATUS is the precedent).
 *
 * FIVE SIGNAL HUES, MORE STATES THAN THAT. There is no sixth hue, so states
 * that mean the same thing share one and the label tells them apart — the
 * Equipment rule. Each map's grouping is written above it.
 */

export interface DhStatusSkin {
  label: string;
  /** Saturated `--fi-signal-*` fill; the badge's ink is always `text-fi-signal-ink`. */
  fill: string;
  /** The row's 4px left rail. */
  rail: string;
}

export const DH_UNKNOWN_SKIN: DhStatusSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

/**
 * dh_component_status. In assembly and In hole are both BLUE — both mean
 * "committed to a string, not free to pick" — and the label separates them.
 * Retired is a dead row: slate pill, steel rail.
 */
export const DH_COMPONENT_STATUS_SKIN: Record<string, DhStatusSkin> = {
  available: { label: 'Available', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  in_assembly: { label: 'In assembly', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  in_hole: { label: 'In hole', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  maintenance: { label: 'Maintenance', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  failed: { label: 'Failed', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  retired: { label: 'Retired', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};

/**
 * dh_assembly_status. Building and Laid down are both AMBER — both mean "in
 * the shop and needing hands" (being made up; back from the well, awaiting
 * teardown). Ready is green, In hole blue as on the component map, and
 * Disassembled is a finished record.
 */
export const DH_ASSEMBLY_STATUS_SKIN: Record<string, DhStatusSkin> = {
  building: { label: 'Building', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  ready: { label: 'Ready', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  in_hole: { label: 'In hole', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  laid_down: { label: 'Laid down', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  disassembled: { label: 'Disassembled', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};

/**
 * dh_work_orders.status. Open and Awaiting parts are both AMBER — waiting on
 * someone — and In progress is blue. Cancelled is a dead row.
 */
export const DH_WORK_ORDER_STATUS_SKIN: Record<string, DhStatusSkin> = {
  open: { label: 'Open', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  in_progress: { label: 'In progress', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  awaiting_parts: { label: 'Awaiting parts', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  completed: { label: 'Completed', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  cancelled: { label: 'Cancelled', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};

/**
 * dh_jobs.status (api#1055). Mobilizing and On location are both BLUE: the
 * crew and tools are committed to the well. Released and Completed are both
 * GREEN: the work at the well is done (Released waits on demobilization).
 * Scheduled is AMBER, booked and not yet moving. Cancelled is a dead row.
 */
export const DH_JOB_STATUS_SKIN: Record<string, DhStatusSkin> = {
  scheduled: { label: 'Scheduled', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  mobilizing: { label: 'Mobilizing', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  on_location: { label: 'On location', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  released: { label: 'Released', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  completed: { label: 'Completed', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  cancelled: { label: 'Cancelled', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};

/** dh_invoices.status. Sent and Approved are both BLUE — out with the customer, not yet paid. */
export const DH_INVOICE_STATUS_SKIN: Record<string, DhStatusSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  sent: { label: 'Sent', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  paid: { label: 'Paid', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  // api#910. Slate: a void invoice claims no money in either direction.
  void: { label: 'Void', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' },
};

/**
 * The PRESENTED invoice status (api#1090, GET /downhole/jobs/:id/billing): the
 * stored vocabulary above plus 'overdue', which that read derives for a sent
 * invoice past its due day. Kept apart from DH_INVOICE_STATUS_SKIN, which
 * mirrors the stored statuses exactly (downhole-labels.test.ts), so the
 * invoice list, which never receives 'overdue', cannot start claiming it.
 */
export const DH_PRESENTED_INVOICE_STATUS_SKIN: Record<string, DhStatusSkin> = {
  ...DH_INVOICE_STATUS_SKIN,
  overdue: { label: 'Overdue', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

export const dhSkin = (map: Record<string, DhStatusSkin>, value: string | null | undefined): DhStatusSkin =>
  (value && Object.prototype.hasOwnProperty.call(map, value) ? map[value] : null) ?? DH_UNKNOWN_SKIN;

/** dh_component_category — all 22 members (0144, 0152c, 0443). */
export const DH_CATEGORY_LABEL: Record<string, string> = {
  drill_collar: 'Drill collar',
  stabilizer: 'Stabilizer',
  jar: 'Jar',
  crossover_sub: 'Crossover sub',
  float_sub: 'Float sub',
  mud_motor: 'Mud motor',
  mwd_tool: 'MWD tool',
  lwd_tool: 'LWD tool',
  reamer: 'Reamer',
  hole_opener: 'Hole opener',
  shock_sub: 'Shock sub',
  non_mag_collar: 'Non-mag collar',
  bit_sub: 'Bit sub',
  pup_joint: 'Pup joint',
  other: 'Other',
  float_collar: 'Float collar',
  centralizer: 'Centralizer',
  shoe: 'Shoe',
  sub: 'Sub',
  packer: 'Packer',
  agitator: 'Agitator',
  fishing_tool: 'Fishing tool',
};

/** dh_assembly_templates.bha_type — the seven the API accepts on POST /templates. */
export const DH_BHA_TYPE_LABEL: Record<string, string> = {
  rotary: 'Rotary',
  motor: 'Motor',
  rss: 'Rotary steerable',
  fishing: 'Fishing',
  coring: 'Coring',
  washover: 'Washover',
  other: 'Other',
};

/** dh_work_orders.work_type (0150 CHECK). An inspection is a work order of this type. */
export const DH_WORK_TYPE_LABEL: Record<string, string> = {
  inspection: 'Inspection',
  repair: 'Repair',
  recertification: 'Recertification',
  hardbanding: 'Hardbanding',
  magnaflux: 'Magnaflux',
  other: 'Other',
};

/** dh_work_orders.priority (0150 CHECK). */
export const DH_PRIORITY_LABEL: Record<string, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  critical: 'Critical',
};

/** dh_disassembly_items.condition (CHECK). */
export const DH_CONDITION_LABEL: Record<string, string> = {
  good: 'Good',
  serviceable: 'Serviceable',
  needs_repair: 'Needs repair',
  failed: 'Failed',
  scrap: 'Scrap',
};

/** dh_disassembly_items.recommend_action (CHECK). */
export const DH_RECOMMEND_ACTION_LABEL: Record<string, string> = {
  return_to_stock: 'Return to stock',
  inspect: 'Inspect',
  repair: 'Repair',
  scrap: 'Scrap',
};

/** dh_disassembly_records.reason (CHECK). */
export const DH_DISASSEMBLY_REASON_LABEL: Record<string, string> = {
  td_reached: 'TD reached',
  failure: 'Failure',
  planned_change: 'Planned change',
  end_of_well: 'End of well',
  other: 'Other',
};

/** GET /components/alerts `reasons[]`. */
export const DH_ALERT_REASON_LABEL: Record<string, string> = {
  wear_hours: 'Hours wear',
  wear_runs: 'Runs wear',
  inspection_overdue: 'Inspection overdue',
};

/**
 * dh_components.last_inspection_result is FREE TEXT (varchar), not an enum.
 * The three values the spec names map to a hue; anything else is shown as
 * written on slate rather than guessed — an unrecognised result is not a Pass.
 */
export const DH_INSPECTION_RESULT_SKIN: Record<string, DhStatusSkin> = {
  pass: { label: 'Pass', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  monitor: { label: 'Monitor', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  fail: { label: 'Fail', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

/** A label from an exhaustive map, or `—` for absent or unrecognised. */
export const dhLabel = (map: Record<string, string>, value: string | null | undefined): string =>
  (value && Object.prototype.hasOwnProperty.call(map, value) ? map[value] : null) ?? '—';

/**
 * For a FREE-TEXT column that usually holds an enum's values
 * (dh_assembly_components.condition_on_return is VARCHAR(20)): a known value
 * gets its label, anything else is printed as written — it is the record, not
 * an unclassifiable status — and absent is `—`.
 */
export const dhLabelOrText = (map: Record<string, string>, value: string | null | undefined): string => {
  if (!value) return '—';
  return Object.prototype.hasOwnProperty.call(map, value) ? map[value] : value;
};

/**
 * dh_run_logs.billing_type — VARCHAR(20), default 'per_cd_run' (0154), with a
 * CHECK on the five values since 0450 (DH_RUN_BILLING_TYPES). Exhaustive over
 * those five; a row predating the CHECK with anything else still prints as
 * written through dhLabelOrText rather than as a guess.
 */
export const DH_BILLING_TYPE_LABEL: Record<string, string> = {
  per_cd_run: 'Per C&D run',
  per_day: 'Per day',
  per_hour: 'Per hour',
  per_foot: 'Per foot',
  hybrid: 'Hybrid',
};

/** dh_job_crew / dh_personnel_rates personnel_role (api#1057), the API's labels. */
export const DH_PERSONNEL_ROLE_LABEL: Record<string, string> = {
  directional_driller: 'Directional driller',
  mwd_operator: 'MWD operator',
  dd_trainee: 'DD trainee',
  mwd_trainee: 'MWD trainee',
  coordinator: 'Coordinator',
};

/** dh_job_crew.shift. Absent is "no shift recorded", rendered as a dash. */
export const DH_CREW_SHIFT_LABEL: Record<string, string> = { day: 'Day', night: 'Night' };

/** GET /jobs/:id/days day_kind (api#1062). An unknown kind is a dash. */
export const DH_DAY_KIND_LABEL: Record<string, string> = { mob: 'Mobilization', on_location: 'On location', demob: 'Demobilization' };

/** A job day's state, the API's vocabulary: operating, standby or off. */
export const DH_DAY_STATE_LABEL: Record<string, string> = { operating: 'Operating', standby: 'Standby', off: 'Off' };

/** A day price line's unit, as the reader says it. */
export const DH_DAY_UNIT_LABEL: Record<string, string> = { day: 'day', foot: 'ft', flat: 'flat' };
